import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Home/HomePage.module.css";
const authenticatedFetch = (url, options = {}) => {
  const token =
    localStorage.getItem("auth_token") || localStorage.getItem("token");
  if (!token) {
    window.location.href = "/#/login";
    throw new Error("No auth token");
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
};
export default function P4ChangePassword() {
  const navigate = useNavigate();
  const API = "https://simplix.co.za/api";

  const [currentPw, setCurrentPw] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Password strength checks (on the NEW password)
  const checks = useMemo(() => {
    return {
      len: pw.length >= 10,
      upper: /[A-Z]/.test(pw),
      lower: /[a-z]/.test(pw),
      digit: /\d/.test(pw),
      special: /[^A-Za-z0-9]/.test(pw),
      nowhite: !/\s/.test(pw),
    };
  }, [pw]);

  const allGood = useMemo(
    () =>
      Object.values(checks).every(Boolean) &&
      pw === pw2 &&
      currentPw.trim() !== "",
    [checks, pw, pw2, currentPw],
  );

  async function submit(e) {
    e.preventDefault();
    setError("");
    setMsg("");

    if (!allGood) {
      setError("Please fill in all fields and meet the password rules.");
      return;
    }

    setLoading(true);
    try {
      const r = await authenticatedFetch(`${API}/profile/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: currentPw, new_password: pw }),
      });

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(data.message || data.error || "Request failed");
      }

      // Success — email has been sent
      setMsg(data.message || "Verification email sent. Check your inbox.");
      // Optionally clear the fields
      setCurrentPw("");
      setPw("");
      setPw2("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.authShell}>
        {/* LEFT */}
        <section className={styles.authIntro}>
          <span className={styles.kicker}>Security</span>
          <h1 className={styles.authHeroTitle}>Update password</h1>
          <p className={styles.authHeroText}>
            Create a strong password to keep your account secure and protected.
          </p>
          <div className={styles.authFeatureGrid}>
            <div className={styles.authFeatureCard}>
              <span>01</span>
              <strong>Confirm it's you</strong>
              <p>
                We'll email you a verification link before applying changes.
              </p>
            </div>
            <div className={styles.authFeatureCard}>
              <span>02</span>
              <strong>Strong password</strong>
              <p>Follow the rules below for maximum security.</p>
            </div>
          </div>
        </section>

        {/* RIGHT */}
        <section className={styles.authCard}>
          <div className={styles.authHeader}>
            <p className={styles.sectionEyebrow}>Account</p>
            <h2>Change password</h2>
          </div>

          <form onSubmit={submit} className={styles.authForm}>
            {/* Current password */}
            <div className={styles.inputWithBtn}>
              <input
                className={styles.authInput}
                type="password"
                placeholder="Current password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
              />
            </div>

            {/* New password */}
            <div className={styles.inputWithBtn}>
              <input
                className={styles.authInput}
                placeholder="New password"
                type={show1 ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
              />
              <button
                type="button"
                className={styles.inlineBtn}
                onClick={() => setShow1((s) => !s)}
              >
                {show1 ? "Hide" : "View"}
              </button>
            </div>

            {/* Confirm new password */}
            <div className={styles.inputWithBtn}>
              <input
                className={styles.authInput}
                placeholder="Confirm new password"
                type={show2 ? "text" : "password"}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
              />
              <button
                type="button"
                className={styles.inlineBtn}
                onClick={() => setShow2((s) => !s)}
              >
                {show2 ? "Hide" : "View"}
              </button>
            </div>

            {/* Rules */}
            <div className={styles.passwordRules}>
              <div className={styles.micro}>Password rules:</div>
              <ul>
                <li className={checks.len ? styles.valid : ""}>
                  At least 10 characters
                </li>
                <li className={checks.upper ? styles.valid : ""}>
                  Uppercase letter
                </li>
                <li className={checks.lower ? styles.valid : ""}>
                  Lowercase letter
                </li>
                <li className={checks.digit ? styles.valid : ""}>Number</li>
                <li className={checks.special ? styles.valid : ""}>
                  Special character
                </li>
                <li className={checks.nowhite ? styles.valid : ""}>
                  No spaces
                </li>
                <li className={pw && pw === pw2 ? styles.valid : ""}>
                  Passwords match
                </li>
              </ul>
            </div>

            {error && <div className={styles.authError}>{error}</div>}
            {msg && <div className={styles.authSuccess}>{msg}</div>}

            <div className={styles.authHelp}>
              <button
                type="button"
                className={styles.inlineBtn}
                onClick={() => navigate(-1)}
              >
                Back
              </button>
              <button
                className={styles.primaryBtn}
                type="submit"
                disabled={!allGood || loading}
              >
                {loading ? "Sending…" : "Request password change"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
